import { TimeSpent } from 'components/ChargePointUptime/interfaces';
import { CurrencyEnum } from 'types/swagger';

import { DECIMALS_FOR_CURRENCY, hasCents } from './hasCents';

export enum StylePricingFormat {
  currency = 'currency',
  decimal = 'decimal',
}

export const formattedNumber = (value: number | string, pad: number, fill: string = '0') => {
  return String(value).padStart(pad, fill);
};

export const calculateTime = (percentage: string, totalHours: number): TimeSpent => {
  const timeBase = Number(percentage) * (totalHours / 100);
  const hours = Math.round(timeBase);
  const minutes = Math.round(timeBase * 60);
  return { hours, minutes };
};

export const escapeRegExp = (value: string) => {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};

export const isValidEmail = (email: string) => {
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
  return emailRegex.test(email);
};

export const getUniqueString = (strings: string[]) => {
  const uniqueStrings = new Set(strings);
  return Array.from(uniqueStrings);
};

// ! Redundant function
export const mapArrayValues = <T, R>(arr: T[], mapFunc: (item: T) => R): R[] => {
  return arr.map(mapFunc);
};
// ! Redundant function
export const checkIfItemExists = <T>(items: T[], itemToCheck: T): boolean => {
  return items?.some((item) => item === itemToCheck);
};

export const formatFileSize = (fileSizeInBytes: number): string => {
  const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  if (fileSizeInBytes === 0) return '0 Byte';

  const i: number = Math.floor(Math.log(fileSizeInBytes) / Math.log(1024));

  return Math.round(fileSizeInBytes / Math.pow(1024, i)) + ' ' + sizes[i];
};

export const toFixedNumber = (value: string | number, fractionDigits?: number) => {
  return parseFloat(String(value)).toFixed(fractionDigits || 2);
};

// Trucate value with the corresponding decimals
export const getApproximatedPrice = (price: string, currency: CurrencyEnum, isGross: boolean) => {
  if (!price) return '---';
  const hasCentsValue = hasCents(currency);
  const numberOfDecimals =
    DECIMALS_FOR_CURRENCY[isGross ? 'gross' : 'net'][
      hasCentsValue ? 'currencyWithCents' : 'currencyWithoutCents'
    ];

  return Number(price).toFixed(numberOfDecimals).toString();
};

export const formatToCurrency = (
  amount: string,
  currency: CurrencyEnum,
  style: StylePricingFormat = StylePricingFormat.decimal,
  isGross: boolean = false
): string => {
  const hasCentsValue = hasCents(currency);
  const decimalSeparator = hasCentsValue ? '.' : ',';
  const thousandsSeparator = hasCentsValue ? ',' : '.';

  const approximatedAmount = getApproximatedPrice(amount, currency, isGross);

  const [integerPart, decimalPart] = approximatedAmount.split('.');

  const integerWithSeparator = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  const result = decimalPart
    ? `${integerWithSeparator}${decimalSeparator}${decimalPart}`
    : integerWithSeparator;

  return style === StylePricingFormat.decimal ? result : `${result} ${currency}`;
};

export const truncateString = (value: string, maxLength: number = 25) => {
  if (value?.length > maxLength) {
    const valueShort = value?.substring(0, maxLength - 1).concat('...');
    return { truncated: true, value: valueShort };
  } else {
    return { truncated: false, value: value || '- - -' };
  }
};

export const validateEmailFormat = (email: string) => {
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return email && regex.test(email.toLowerCase());
};
